<template>
  <div id="chat-component">
    <ul id="chat-tab-buttons" class="nav nav-tabs">
      <li :class="{active: currentTab === tabs.CHAT}">
        <a @click="currentTab = tabs.CHAT">Chat</a>
      </li>
      <li :class="{active: currentTab === tabs.PARTICIPANTS}">
        <a @click="currentTab = tabs.PARTICIPANTS">Participants ({{ numActive }})</a>
      </li>
      <li v-if="isManager"
          :class="{active: currentTab === tabs.SETTINGS}"
          class="tabbed-icon"
      >
        <a @click="currentTab = tabs.SETTINGS"
           class="manager-tab"
           title="Chat Moderation (Manager-only)"
        >
          <Admin/>
        </a>
      </li>
    </ul>
    <div v-if="currentTab === tabs.CHAT"
         id="chat-tab-msg"
    >
      <div id="chat-component-log">
        <ChatMessage v-for="(message, index) in messages" :key="message.created_at"
                     :message="message"
                     :prevMessage="messages[index - 1]"
                     :status="statuses[message.sender_id]"
                     :myId="myId"
                     :isModerating="isModerating"
                     :canBan="canBan"
                     @delete="sendDelete"
                     @ban="sendBan"
        />
      </div>
      <form class="chat-component-message-widget">
                <textarea v-model="message"
                          :disabled="!hasSocket"
                          v-on:keyup.enter.exact="sendMessage"
                          placeholder="Type a message..."
                          name="chat-input" rows="2"></textarea>
        <button @click="sendMessage"
                :disabled="!canSubmit"
                id="chat-component-message-submit"
                class="btn btn-primary btn-large"
                type="button"
        >Send
        </button>
      </form>
    </div>
    <div v-if="currentTab === tabs.PARTICIPANTS"
         id="chat-tab-status">
      <ChatStatusAttendee v-for="(status, index) in participantsActive" :key="index"
                          :status="status"
      />
      <div class="chat-status-absent">
        <ChatStatusAttendee v-for="(status, index) in participantsGone" :key="index"
                            :status="status"
        />
      </div>
      <div class="chat-status-banned">
        <ChatStatusAttendee v-for="(status, index) in participantsBanned" :key="index"
                            :status="status"
        />
      </div>
    </div>
    <div v-if="isManager && currentTab === tabs.SETTINGS"
         id="chat-tab-settings">
      <div class="chat-tab-inner">
        <h5 class="text">Chat Moderation (Manager-only)</h5>
        <div class="moderation-block">
          <h4>Enable Moderation</h4>
          <p>Allows you to delete individual chat messages. Defaults to 'OFF' for safety.</p>

          <SwitchLegacy
              @click="isModerating = !isModerating"
              :value="isModerating"
              :title="'Enable moderation'"
          />
        </div>
        <div class="moderation-block">
          <h4>Wipe all messages</h4>
          <p>This will wipe the <b>entire</b> message history of
            <b>{{ messages.length }}{{ messages.length > 50 ? '+' : '' }}</b>
            messages for this chat.</p>
          <p>You almost certainly don't want to do this, unless you're resetting the display after testing.</p>

          <ButtonWithInlineConfirmation v-if="messages.length"
                                        @click="sendDeleteAll">
            <template #button>Wipe chat...</template>
            <template #confirm>Yes - Wipe all</template>
          </ButtonWithInlineConfirmation>
          <button v-else class="btn btn-danger disabled">Wipe chat...</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex';

import {CHAT_TYPES, CHAT_SUBTYPES} from "../Config";
import ButtonWithInlineConfirmation from "@/components/shared/ButtonWithInlineConfirmation";
import ChatMessage from "./ChatMessage";
import ChatStatusAttendee from "./ChatStatusAttendee";
import EventService from "../../services/EventService";
import Admin from "@/components/icons/Admin";
import SwitchLegacy from "@/components/shared/SwitchLegacy";
import {compareSenderName} from "../Utils";

export const tabs = {
  CHAT: 'chat',
  PARTICIPANTS: 'participants',
  SETTINGS: 'settings',
};

export default {
  name: "ChatComponent",
  components: {
    Admin,
    ButtonWithInlineConfirmation,
    ChatMessage,
    ChatStatusAttendee,
    SwitchLegacy,
  },
  props: {
    url: String,
    canBan: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      socket: null,
      error: null,
      messages: [],
      statuses: {},
      message: '',
      hasSocket: false,
      isDeleted: false,
      isReady: false,  // Don't trigger alerts until we're 'ready'
      isModerating: false,
      currentTab: tabs.CHAT,
    }
  },
  computed: {
    myId() {
      return EventService.jwtPayloadAttendeeId();
    },
    canSubmit() {
      if (!this.hasSocket || this.isDeleted) {
        return false;
      }

      return this.message.length > 0;
    },
    participantsActive() {
      let matches = [];
      Object.values(this.statuses).forEach(status => {
        if (status.subtype === CHAT_SUBTYPES.JOIN) {
          matches.push(status);
        }
      });
      return matches.sort(compareSenderName);
    },
    participantsGone() {
      let matches = [];
      Object.values(this.statuses).forEach(status => {
        if (status.type === CHAT_TYPES.MESSAGE && status.subtype === CHAT_SUBTYPES.LEAVE) {
          matches.push(status);
        }
      });
      return matches.sort(compareSenderName);
    },
    participantsBanned() {
      let matches = [];
      Object.values(this.statuses).forEach(status => {
        if (status.type === CHAT_TYPES.BANNED) {
          matches.push(status);
        }
      });
      return matches.sort(compareSenderName);
    },
    numActive() {
      return this.participantsActive.length;
    },
    ...mapGetters({
      isManager: 'isManager',
    }),
  },
  methods: {
    sendMessage() {
      if (this.message) {
        this.socket.send(JSON.stringify({
          'message': this.message
        }));

        this.message = '';
      }
    },
    sendPointer(messageId) {
        this.socket.send(JSON.stringify({
            type: CHAT_SUBTYPES.READY,
            pointer: messageId
        }));
    },
    sendDelete(messageId) {
      console.log("DELETE:", messageId);
      if (messageId) {
        this.socket.send(JSON.stringify({
          'type': CHAT_TYPES.DELETE,
          'message_id': messageId
        }));
      }
    },
    sendDeleteAll() {
      this.socket.send(JSON.stringify({
        'type': CHAT_TYPES.DELETE_ALL,
      }));
    },
    sendBan(attendeeId) {
      console.log("BAN:", attendeeId);
      if (attendeeId) {
        this.socket.send(JSON.stringify({
          'type': CHAT_TYPES.BANNED,
          'attendee_id': attendeeId
        }));
      }
    },
    scrollToBottom() {
      console.log("Scrolling...");
      const objDiv = document.getElementById("chat-component-log");
      if (objDiv) {
        objDiv.scrollTop = objDiv.scrollHeight;
      }
    },
  },
  mounted: function () {
    if (!this.url) {
      this.error = "URL endpoint missing";
      return false;
    }

    this.socket = new WebSocket(
        'wss://'
        + window.location.host
        + this.url
    );
    this.hasSocket = true;

    this.socket.onmessage = (e) => {
      const data = JSON.parse(e.data);
      if (data.subtype === CHAT_SUBTYPES.MESSAGE) {
        this.messages.push(data);

        // 'new-message' event so that we can record the data in $store.messages.latest
        this.$emit('new-message', data);
      } else if (data.subtype === CHAT_SUBTYPES.CLOSED) {
        this.isDeleted = true;
        this.messages.push({
          subtype: CHAT_SUBTYPES.CLOSED,
          message: "This chat is no longer active.",
        });

      } else if (data.subtype === CHAT_SUBTYPES.READY) {
        // After this we can alert on new incoming messages
        // TODO: Add email/staff visibility state to message.
        if (data.pointers) {
          console.log("ChatComponent data.pointers", data.pointers);
          // this.addPointers(data.pointers);
        }
        if (!this.isReady) {
          // We've just switched to this thread.
          // All existing messages have been sent, so assume attendee has now read them
          let last = this.messages[this.messages.length -1];
          if (last) {
            console.log("Sending pointer:", last);
            this.sendPointer(last.id);
          }
        }

        this.isReady = true;

      } else if (data.type === CHAT_TYPES.DELETE) {
        console.log("Received delete:", data);
        const index = this.messages.findIndex(msg => msg.id === data.id);
        if (index !== -1) {
          this.messages.splice(index, 1);
        }

      } else if (data.type === CHAT_TYPES.BANNED) {
        // Test if this is referring to yourself
        if (this.myId == data.sender_id) {
          console.log("I have been banned!");
          location.reload(); // PHP should trigger a redirect action+banned message
        } else {
          this.$set(this.statuses, data.sender_id, data);
        }

      } else {
        // All other subtypes handled as status variants
        this.$set(this.statuses, data.sender_id, data);
      }
    };

    this.socket.onerror = (e) => {
      this.error = e;
      console.error('Chat socket error:', e);
      this.messages.push({
        type: CHAT_TYPES.ERROR,
        sender_name: "Chat error",
        message: "Please reload the page to continue.",
      });
    };

    this.socket.onclose = (e) => {
      console.log('Chat socket closed', e);
      this.hasSocket = false;
      this.messages.push({
        type: CHAT_TYPES.SOCKET_CLOSED,
        sender_name: "This chat has gone to sleep.",
        message: "Please reload the page to continue.",
      });
    };
  },
  updated: function () {
    this.$nextTick(this.scrollToBottom());
  },
  created() {
    this.tabs = tabs;
  }
}
</script>

<style>

#chat-component {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  padding-top: 5px;
  padding-bottom: 0;
}

#chat-tab-buttons {
  margin-bottom: 0;
}

#chat-tab-buttons a {
  cursor: pointer;
}

#chat-tab-msg,
#chat-tab-status,
#chat-tab-settings {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  background-color: #fff;
  border-left: 1px solid #ddd;
  height: calc(100% - 40px); /* ~height of chat-tab-buttons */
}

#chat-tab-status {
  overflow-y: auto;
}

#chat-tab-settings {
  background-color: #000;
  color: #fff;
}

.chat-tab-inner {
  padding: 5px;
}

#chat-component-log {
  box-sizing: border-box;
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  padding: 10px;
  overflow-y: auto;
  height: calc(100% - 80px); /* 60px form widget + 10px padding */
}

form.chat-component-message-widget {
  align-items: stretch;
  background-color: #ddd;
  display: flex;
  margin: 0;
  padding: 10px;
}

.chat-component-message-widget textarea {
  margin-bottom: 0;
  flex-grow: 1;
}

#chat-component .tabbed-icon svg {
  height: 20px;
  width: 20px;
}

#chat-component a.manager-tab {
  color: #000;
}

#chat-component .nav > li > a.manager-tab:hover {
  background-color: #111;
  color: #eee;
}

#chat-component .nav-tabs > .active a.manager-tab {
  background-color: #000;
  color: #fff;
}

#chat-component .moderation-block {
  border-top: 1px solid #666;
  margin-bottom: 30px;
  padding: 10px;
}
</style>